/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : PageHeader
 *   Feature      : 197-portal-light-theme-responsive
 *   Description  : Canonical page header — one consistent title / description /
 *                  actions block for every screen. Token-styled and
 *                  framework-neutral (plain markup + Tailwind tokens) so it drops
 *                  into both MUI and Tailwind pages. Replaces the ad-hoc mix of
 *                  `Typography h4` / `<h1 text-2xl>` / `text-3xl` headers.
 *                  Responsive: title block + actions stack on mobile, row on ≥sm.
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 *   Created      : 2026-06-14
 * ============================================================================
 */
import React from 'react';
import { RefreshButton } from '../ui/RefreshButton';
import { PageHelpButton } from '../ui/PageHelpButton';

export interface PageHeaderProps {
  /** Page title (rendered as the page's single h1). */
  title: React.ReactNode;
  /** Optional one-line subtitle / description. */
  description?: React.ReactNode;
  /** Optional leading icon. */
  icon?: React.ReactNode;
  /** Optional right-aligned actions (buttons, help panel, etc.). */
  actions?: React.ReactNode;
  /** Extra classes on the wrapper. */
  className?: string;

  // ── Spec 226 — Universal Page Affordances (all optional; absent = not rendered) ──
  /** Registry key for this page's contextual help; renders the `?` panel when set. */
  helpId?: string;
  /** Soft re-fetch action; renders the standard Refresh control when set. */
  onRefresh?: () => void | Promise<void>;
  /** True while a refresh is in flight (spins the icon, disables the button). */
  isRefreshing?: boolean;
  /** Last client fetch time (freshness indicator). */
  lastFetchedAt?: Date | number | string | null;
  /** DB collection time, best-effort (freshness indicator). */
  collectedAt?: Date | number | string | null;
  /** Last refresh error; renders the non-blocking error badge. */
  refreshError?: unknown;
}

/**
 * Consistent page header used at the top of every screen.
 * Renders the standard affordance cluster — [Refresh + freshness] [help ?] —
 * before any page-specific `actions` (Spec 226).
 */
export function PageHeader({
  title,
  description,
  icon,
  actions,
  className,
  helpId,
  onRefresh,
  isRefreshing,
  lastFetchedAt,
  collectedAt,
  refreshError,
}: PageHeaderProps) {
  const hasAffordances = onRefresh != null || helpId != null;

  return (
    <div
      className={`mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between ${
        className ?? ''
      }`}
    >
      <div className="flex min-w-0 items-start gap-3">
        {icon ? (
          <div className="mt-0.5 flex-shrink-0 text-[var(--color-accent)]">{icon}</div>
        ) : null}
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold leading-tight text-[var(--color-text-primary)]">
            {title}
          </h1>
          {description ? (
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{description}</p>
          ) : null}
        </div>
      </div>
      {hasAffordances || actions ? (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
          {onRefresh ? (
            <RefreshButton
              onRefresh={onRefresh}
              isRefreshing={isRefreshing}
              lastFetchedAt={lastFetchedAt}
              collectedAt={collectedAt}
              error={refreshError}
            />
          ) : null}
          {helpId ? <PageHelpButton helpId={helpId} /> : null}
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export default PageHeader;
